import React from "react";
import {
  Box,
  SimpleGrid,
  Flex,
  Text,
  Heading,
  Icon,
  VStack,
  HStack,
  Badge,
  Button,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  CarFront,
  Hotel,
  Package,
  Settings,
  Boxes,
  ClipboardList,
  ChevronRight,
  Route,
  Search,
  Wallet,
  Ticket,
  UserCog,
  Users,
  RefreshCw,
  LineChart,
  Lock,
  LogIn,
} from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAuth } from "../../auth/AuthContext";
import { db } from "../../configs/firebase";
import { normalizeUserRole, USER_ROLES } from "../Profile/constants/userRoles";

type ManageItem = {
  id: number;
  title: string;
  subtitle: string;
  path: string;
  icon: React.ElementType;
  color: string;
};

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ManagePage = () => {
  const navigate = useNavigate();
  const { theme } = useCustomTheme();
  const { user } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [hasAdminAccess, setHasAdminAccess] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    const loadCurrentUserRole = async () => {
      if (!user?.uid) {
        if (active) {
          setIsSuperAdmin(false);
          setHasAdminAccess(false);
        }
        return;
      }

      try {
        const userSnapshot = await getDoc(doc(db, "users", user.uid));
        const role = userSnapshot.exists()
          ? normalizeUserRole(userSnapshot.data().role)
          : USER_ROLES.GUEST;

        if (active) {
          setIsSuperAdmin(role === USER_ROLES.SUPER_ADMIN);
          setHasAdminAccess(
            role === USER_ROLES.ADMIN || role === USER_ROLES.SUPER_ADMIN,
          );
        }
      } catch (error) {
        console.error("Failed to verify administrator access:", error);

        if (active) {
          setIsSuperAdmin(false);
          setHasAdminAccess(false);
        }
      }
    };

    loadCurrentUserRole();

    return () => {
      active = false;
    };
  }, [user?.uid]);

  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    deepSlate: "#2a2c3b",
    deepInk: "#1a1c2f",
    slate2: "#2e3143",
    softLight: "#eaebf0",
    violet: "#b05cff",
    indigo: "#6f7cff",
  };

  const gradients = theme.gradients || {
    pageBg: theme.background,
    shellBg: theme.surface,
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
    secondaryButton: `linear-gradient(135deg, ${brand.indigo} 0%, ${brand.electricBlue} 100%)`,
    secondaryButtonHover: `linear-gradient(135deg, #6170ff 0%, #20b8ff 100%)`,
    tabHikesSelected: `linear-gradient(135deg, rgba(39,183,255,0.14) 0%, rgba(73,209,255,0.08) 100%)`,
    tabDeliverySelected: `linear-gradient(135deg, rgba(111,124,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
    tabShopSelected: `linear-gradient(135deg, rgba(176,92,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
  };

  const pageBg = gradients.pageBg || theme.background;
  const shellBg = gradients.shellBg || theme.surface;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const panelShadow = theme.shadowMd;
  const softShadow = theme.shadowSm;
  const cardsBorderRadii ="8px";

  const manageItems: ManageItem[] = [
    {
      id: 1,
      title: "Products Manager",
      subtitle: "Create and manage products",
      path: "/manage/products",
      icon: ShoppingCart,
      color: "#10B981",
    },
    {
      id: 2,
      title: "Hiking Manager",
      subtitle: "Trips and hiking operations",
      path: "/hike-manager",
      icon: Route,
      color: "#F59E0B",
    },
    {
      id: 3,
      title: "Car Hire Manager",
      subtitle: "Manage vehicles and hire flow",
      path: "/hire-manager",
      icon: CarFront,
      color: "#8B5CF6",
    },
    {
      id: 4,
      title: "Accommodation Manager",
      subtitle: "Manage stays and bookings",
      path: "/travel/Accommodation-manager",
      icon: Hotel,
      color: "#EC4899",
    },
    {
      id: 5,
      title: "Orders Admin",
      subtitle: "Orders and fulfillment",
      path: "/admin/orders",
      icon: ClipboardList,
      color: "#2563EB",
    },
    {
      id: 6,
      title: "Delivery Manager",
      subtitle: "Deliveries and tracking",
      path: "/delivery/list",
      icon: Package,
      color: "#F97316",
    },
    {
      id: 7,
      title: "Finance Admin",
      subtitle: "Finance hub and payouts",
      path: "/finance/admin",
      icon: Wallet,
      color: "#0F766E",
    },
    {
      id: 8,
      title: "Seller Finance",
      subtitle: "Seller earnings dashboard",
      path: "/finance/seller",
      icon: Wallet,
      color: "#16A34A",
    },
    {
      id: 9,
      title: "Driver Finance",
      subtitle: "Driver earnings dashboard",
      path: "/finance/driver",
      icon: Wallet,
      color: "#DC2626",
    },
    {
      id: 10,
      title: "Driver Ops",
      subtitle: "Drivers, dispatch and assignments",
      path: "/drivers/admin",
      icon: UserCog,
      color: "#0891B2",
    },
    {
      id: 11,
      title: "Analytics",
      subtitle: "Application performance and insights",
      path: "/analytics",
      icon: LineChart,
      color: "#6366F1",
    },
    {
      id: 12,
      title: "Support Tickets",
      subtitle: "Customer support and tickets",
      path: "/support/tickets",
      icon: Ticket,
      color: "#A855F7",
    },
    ...(hasAdminAccess
      ? [
          {
            id: 13,
            title: "Marketplace Sync",
            subtitle: "Discover, verify and publish marketplace products",
            path: "/admin/marketplace-sync",
            icon: RefreshCw,
            color: "#0EA5E9",
          },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          {
            id: 14,
            title: "Users Admin",
            subtitle: "Manage users, roles and accounts",
            path: "/admin/users",
            icon: Users,
            color: "#7C3AED",
          },
        ]
      : []),
  ];

  return (
    <Box h="100%" minH="100%" w="100%" bg={pageBg}>
      <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
        <Box
          flex="1"
          minH={0}
          w="100%"
          borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
          bg={shellBg}
          boxShadow={panelShadow}
          p={{ base: 3, md: 4 }}
          backdropFilter={isDark ? "blur(10px)" : undefined}
        >
          <Box
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={softShadow}
            border={`1px solid ${theme.border}`}
            p={{ base: 4, md: 5 }}
            mb={4}
            position="relative"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    color={isDark ? "blue.200" : "blue.700"}
                    fontWeight="700"
                  >
                    Manage
                  </Badge>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(176,92,255,0.14)" : "rgba(176,92,255,0.08)"
                    }
                    color={isDark ? "purple.200" : "purple.700"}
                    fontWeight="700"
                  >
                    Admin Tools
                  </Badge>
                  {!user && (
                    <Badge
                      px={3}
                      py={1}
                      borderRadius={cardsBorderRadii}
                      bg={
                        isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.10)"
                      }
                      color={isDark ? "red.200" : "red.700"}
                      fontWeight="700"
                    >
                      Login required
                    </Badge>
                  )}
                </HStack>

                <Heading size="lg" color={textColor}>
                  Manage your platform operations
                </Heading>
                <Text mt={2} color={mutedText} fontWeight="500" maxW="760px">
                  Open all your management tools from one place — products,
                  orders, finance, delivery, drivers, analytics, and support.
                </Text>
              </Box>

              <HStack
                spacing={2}
                px={3}
                py={2}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${theme.border}`}
                bg={isDark ? "rgba(255,255,255,0.03)" : softBg}
                boxShadow={softShadow}
              >
                <Box
                  w="8px"
                  h="8px"
                  borderRadius={cardsBorderRadii}
                  bg={brand.electricBlue}
                />
                <Text color={textColor} fontWeight="700" fontSize="sm">
                  {manageItems.length} tools
                </Text>
              </HStack>
            </Flex>
          </Box>

          {!user ? (
            <Box
              borderRadius={cardsBorderRadii}
              bg={cardBg}
              boxShadow={softShadow}
              border={`1px solid ${theme.border}`}
              p={{ base: 5, md: 6 }}
            >
              <VStack spacing={4} align="start">
                <Flex
                  w="52px"
                  h="52px"
                  borderRadius={cardsBorderRadii}
                  align="center"
                  justify="center"
                  bg={isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}
                  border={`1px solid ${isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"}`}
                >
                  <Icon
                    as={Lock}
                    boxSize={6}
                    color={isDark ? "red.200" : "red.600"}
                  />
                </Flex>

                <Box>
                  <Text color={textColor} fontWeight="800" fontSize="lg">
                    You need to be logged in
                  </Text>
                  <Text color={mutedText} mt={1}>
                    Sign in to view and access management tools.
                  </Text>
                </Box>

                <HStack spacing={3} pt={1} flexWrap="wrap">
                  <Button
                    leftIcon={<LogIn size={16} />}
                    color={theme.textOnPrimary}
                    bgImage={theme.gradients?.primaryButton || theme.primary}
                    _hover={{
                      bgImage:
                        theme.gradients?.primaryButtonHover ||
                        theme.primaryHover,
                      transform: "translateY(-1px)",
                    }}
                    _active={{ transform: "translateY(0)" }}
                    borderRadius={cardsBorderRadii}
                    boxShadow={theme.shadowSm}
                    transition="all 0.2s ease"
                    onClick={() => navigate("/login")}
                  >
                    Sign In
                  </Button>

                  <Button
                    variant="outline"
                    borderColor={theme.borderStrong}
                    color={textColor}
                    bg={theme.surface}
                    _hover={{ bg: theme.hover }}
                    borderRadius={cardsBorderRadii}
                    onClick={() => navigate("/dashboard")}
                  >
                    Go back
                  </Button>
                </HStack>
              </VStack>
            </Box>
          ) : (
            <>
              <SimpleGrid
                columns={{ base: 2, sm: 2, md: 3, lg: 4, xl: 6 }}
                spacing={{ base: 3, md: 3 }}
              >
                {manageItems.map((item) => (
                  <Box
                    key={item.id}
                    role="group"
                    cursor="pointer"
                    onClick={() => navigate(item.path)}
                    borderRadius={cardsBorderRadii}
                    bg={cardBg}
                    boxShadow={softShadow}
                    border={`1px solid ${theme.border}`}
                    p={{ base: 3, md: 3.5 }}
                    transition="all 0.25s ease"
                    _hover={{
                      transform: "translateY(-3px)",
                      boxShadow: panelShadow,
                      borderColor: hexToRgba(item.color, 0.34),
                      bg: isDark
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(255,255,255,0.98)",
                    }}
                    minH="108px"
                  >
                    <Flex align="center" justify="space-between" mb={2.5}>
                      <Flex
                        w="44px"
                        h="44px"
                        borderRadius={cardsBorderRadii}
                        align="center"
                        justify="center"
                        bg={hexToRgba(item.color, isDark ? 0.18 : 0.12)}
                        border={`1px solid ${hexToRgba(item.color, isDark ? 0.3 : 0.22)}`}
                        boxShadow={softShadow}
                      >
                        <Icon
                          as={item.icon}
                          boxSize={5}
                          color={item.color}
                          fill="transparent"
                          strokeWidth={2.1}
                        />
                      </Flex>

                      <Flex
                        w="28px"
                        h="28px"
                        borderRadius={cardsBorderRadii}
                        align="center"
                        justify="center"
                        bg={isDark ? "rgba(255,255,255,0.04)" : softBg}
                        _groupHover={{
                          bg: hexToRgba(item.color, isDark ? 0.16 : 0.1),
                        }}
                        transition="all 0.2s ease"
                      >
                        <Icon
                          as={ChevronRight}
                          boxSize={4}
                          color={item.color}
                        />
                      </Flex>
                    </Flex>

                    <Text
                      color={textColor}
                      fontWeight="800"
                      fontSize="sm"
                      noOfLines={1}
                    >
                      {item.title}
                    </Text>

                    <Text
                      color={mutedText}
                      fontSize="xs"
                      mt={1}
                      noOfLines={2}
                      lineHeight="short"
                    >
                      {item.subtitle}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>

              <Box
                mt={4}
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                boxShadow={softShadow}
                border={`1px solid ${theme.border}`}
                p={{ base: 4, md: 4 }}
              >
                <HStack spacing={3}>
                  <Flex
                    w="42px"
                    h="42px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    border={`1px solid ${isDark ? "rgba(39,183,255,0.22)" : "rgba(39,183,255,0.16)"}`}
                  >
                    <Icon as={Search} boxSize={5} color={brand.electricBlue} />
                  </Flex>
                  <Box>
                    <Text color={textColor} fontWeight="700">
                      Fast management access
                    </Text>
                    <Text color={subtleText} fontSize="sm">
                      Tap any card to open that management section directly.
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default ManagePage;
